<template>
    <el-date-picker
        :id="id"
        ref="datepicker"
        v-bind="$attrs"
        :end-placeholder="$t('form.date-range.end-placeholder')"
        :format="formatConverted"
        :clearable="clearable"
        :picker-options="pickerOptions"
        :popper-class="reportingShortcuts ? 'payment-reporting-date-range-picker' : ''"
        :range-separator="$t('form.date-range.range-separator')"
        :start-placeholder="$t('form.date-range.start-placeholder')"
        :value="value"
        class="form-daterangepicker"
        :class="{'form-daterangepicker--clearable': clearable}"
        type="daterange"
        @input="$emit('input', fixDates($event || []))"
    />
</template>

<script>
    import DatePicker from 'element-ui/lib/date-picker';
    import DateFormat from '../../mixins/Inputs/DateFormat';
    import ReportingDateRanges from '../../mixins/Inputs/ReportingDateRanges';
    import moment from 'moment';

    let counter = 0;

    export default {
        mixins: [DateFormat, ReportingDateRanges],

        components: {
            'el-date-picker': DatePicker,
        },

        props: {
            id: {
                type: String,
                default: 'daterangepicker_' + ++counter,
            },

            value: {
                type: Array,
                default() {
                    return [];
                },
            },

            clearable: {
                type: Boolean,
                default: true,
            },

            reportingShortcuts: {
                type: Boolean,
                default: false,
            },

        },

        computed: {
            pickerOptions() {
                return {
                    firstDayOfWeek: 1,
                    shortcuts: this.shortcuts,
                };
            },

            shortcuts() {
                if (this.reportingShortcuts) {
                    return this.reportingDateRangeGroups.reduce((shortcuts, group) => (
                        shortcuts.concat(group.ranges.map(range => ({
                            text: range.label,
                            onClick: (picker) => picker.$emit('pick', this.fixDates(
                                range.value.map(date => new Date(date))
                            )),
                        })))
                    ), []);
                }

                const mockDate = window.sessionStorage.getItem('MOCK_DATE');
                const today = mockDate ? new Date(mockDate) : new Date();

                return [
                    ...this.createMonthsShortcuts(today),
                    {
                        text: '──────',
                        onClick() {},
                    },
                    ...this.createQuartersShortcuts(today),
                    {
                        text: '──────',
                        onClick() {},
                    },
                    ...this.createYearShortcuts(today),
                ];
            },
        },

        mounted() {
            // Focus input when the associated label is clicked
            document.addEventListener('click', (e) => {
                if (!this.id || e.target.tagName !== 'LABEL' || e.target.htmlFor !== this.id) {
                    return;
                }

                this.$refs.datepicker && this.$refs.datepicker.focus();
            });

            Array.from(this.$refs.datepicker.$el.querySelectorAll('input')).forEach(input => {
                input.autocomplete = 'off';
            });
        },

        methods: {
            // Fix timezone offset
            fixDates(dates) {
                return dates.map(date => {
                    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());

                    return date;
                });
            },

            createMonthsShortcuts(today) {
                // last 12 months (including current month)
                return Array.from(Array(12).keys()).map(month => {
                    const startDate = moment(today)
                        .startOf('month')
                        .subtract(month, 'months');
                    const endDate = startDate
                        .clone()
                        .endOf('month')
                        .startOf('day');
                    const yearShort = startDate.year().toString().slice(-2);

                    return ({
                        text: `${this.$d(startDate, 'monthShort')} '${yearShort}`,
                        onClick: (picker) => picker.$emit('pick', this.fixDates(
                            [
                                startDate.toDate(),
                                endDate.toDate(),
                            ])),
                    });
                });
            },

            createQuartersShortcuts(today) {
                // last 4 quarters (including current quarter):
                const startDateCurrentQuarter = moment(today)
                    .startOf('month')
                    .subtract(today.getMonth() % 3, 'months');

                return Array.from(Array(4).keys()).map(index => {
                    const startDate = startDateCurrentQuarter
                        .clone()
                        .subtract(index, 'quarter')
                        .startOf('day');
                    const endDate = startDate
                        .clone()
                        .add(1, 'quarter')
                        .subtract(1, 'day')
                        .startOf('day');

                    const quarter = Math.floor(startDate.month() / 3);
                    const yearShort = startDate.year().toString().slice(-2);

                    return {
                        text: `Q${quarter + 1} '${yearShort}`,
                        onClick: (picker) => picker.$emit('pick', this.fixDates([startDate.toDate(), endDate.toDate()])),
                    };
                });
            },

            createYearShortcuts(today) {
                // last 2 years (including current year)
                return Array.from(Array(2).keys()).map(year => ({
                    text: today.getFullYear() - year,
                    onClick: (picker) => picker.$emit('pick', this.fixDates(
                        [
                            new Date(today.getFullYear() - year, 0, 1),
                            new Date(today.getFullYear() - year, 11, 31),
                        ])),
                }));
            },
        },
    };
</script>
